import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus,
  Search,
  Edit,
  Trash2,
  Mail,
  Phone,
  Loader2,
  AlertCircle,
  Users as UsersIcon
} from 'lucide-react';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';
import { useQuickActions } from '../contexts/QuickActionsContext';
import CreateUserModal from '../components/modals/user/CreateUserModal';
import EditUserModal from '../components/modals/user/EditUserModal';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import { toast } from '@/components/ui/sonner';
import type { AppDispatch } from '../store/store';
import {
  fetchUsers,
  searchUsers,
  createUser,
  updateUser,
  deleteUser,
  setSearchTerm,
  clearError,
  selectFilteredUsers,
  selectUsersLoading,
  selectUsersError,
  selectSearchTerm,
  selectIsSearching,
  selectUsersTotal
} from '../store/slices/usersSlice';
import { selectCurrentClinic, selectClinicsLoading } from '../store/slices/authSlice';
import { usersApi, type User } from '../services/usersService';
import type { Api } from '../types/generated';

const Users = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openCreateUserModal } = useQuickActions();
  
  // Redux selectors
  const users = useSelector(selectFilteredUsers);
  const loading = useSelector(selectUsersLoading);
  const isSearching = useSelector(selectIsSearching);
  const error = useSelector(selectUsersError);
  const searchTerm = useSelector(selectSearchTerm);
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const total = useSelector(selectUsersTotal);

  // Local UI state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          dispatch(searchUsers(searchTerm));
        }, 300); // 300ms debounce
      };
    })(),
    [dispatch]
  );

  // Fetch users on component mount and when clinic changes
  useEffect(() => {
    // GATE: Wait for clinic loading to complete
    if (clinicsLoading) {
      console.log('[Users] Waiting for clinics to load...');
      return;
    }

    // GATE: Ensure we have clinic data with users href
    if (!currentClinic?.users?.href) {
      console.log('[Users] No users href available yet');
      return;
    }

    // NOW SAFE: Make API call
    console.log('[Users] Loading users from:', currentClinic.users.href);
    dispatch(fetchUsers({}));
  }, [dispatch, currentClinic, clinicsLoading]);

  // Handle search term changes with server-side search
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Clear error when component unmounts
  // Check for URL parameters to open modals
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      // Remove the parameter from URL to clean up
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);


  // Handle edit user - fetch full details first
  const handleEditUser = async (user: User) => {
    setLoadingUserDetails(true);
    try {
      // Fetch full user details using HATEOAS href
      const fullUser = await usersApi.getUser(user.href);
      setEditingUser(fullUser);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      // Still open modal with existing data if fetch fails
      setEditingUser(user);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  // Handle update user
  const handleUpdateUser = async (userId: string, userData: Partial<Api.User>) => {
    console.log('[Users] handleUpdateUser called', { userId, userData });
    try {
      // Use the editingUser directly since it has the correct href
      if (!editingUser) {
        throw new Error('No user being edited');
      }
      
      console.log('[Users] Using editingUser, dispatching update', { userHref: editingUser.href, userData });
      
      await dispatch(updateUser({ 
        userHref: editingUser.href, 
        userData 
      })).unwrap();
      setEditingUser(null);
      
      // Show success toast
      const userName = userData.name || editingUser.name;
      toast.success(`${userName} updated successfully`);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user. Please try again.');
    }
  };

  // Handle delete user
  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    const userName = userToDelete.name;
    setIsDeleting(true);
    
    try {
      await dispatch(deleteUser({ 
        userId: userToDelete.id,
        userHref: userToDelete.href 
      })).unwrap();
      
      // Show success toast
      toast.success(`${userName} deleted successfully`);
    } catch (error) {
      console.error('Failed to delete user:', error);
      // Show error toast
      toast.error(`Failed to delete ${userName}. Please try again.`);
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setUserToDelete(null);
    setIsDeleting(false);
  };

  // Helper function to get role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Owner':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Manager':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Prescriber':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Staff':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:pl-24 lg:pr-8 py-8">
        {/* Header Section */}
        <div className="flex flex-col space-y-4 mb-8 md:flex-row md:justify-between md:items-center md:space-y-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-syne font-medium text-vitl-navy mb-2">User Management</h1>
            <p className="text-vitl-dark/70 font-lato text-sm md:text-base">Manage platform users and their permissions.</p>
          </div>
          <Button 
            onClick={openCreateUserModal}
            className="bg-vitl-green hover:bg-vitl-green/90 text-white font-lato font-medium self-start md:self-auto"
            disabled={loading}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New User
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => dispatch(clearError())}
              className="ml-auto"
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="mb-6 border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-vitl-green w-4 h-4 animate-spin" />
                )}
                <Input 
                  placeholder="Search users by name, email, or role..."
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                  className={`pl-10 font-lato ${isSearching ? 'pr-10' : ''}`}
                />
              </div>
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {isSearching ? 'Searching...' : `Found ${total} user${total !== 1 ? 's' : ''}`}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Loading State - show during clinic loading OR users loading */}
        {(loading || clinicsLoading) && !searchTerm && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-vitl-green" />
            <span className="ml-2 text-gray-600">
              {clinicsLoading ? 'Loading clinic data...' : 'Loading users...'}
            </span>
          </div>
        )}

        {/* Users List */}
        {((!loading && !clinicsLoading) || searchTerm) && (
          <div className="space-y-4">
            <div className="mb-4">
              <h2 className="text-lg font-syne font-medium text-vitl-navy">
                All Users ({total})
              </h2>
            </div>
            {users.map((user) => (
              <Card key={user.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                <CardContent className="p-6">
                  <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-vitl-green rounded-full flex items-center justify-center text-white font-syne font-medium flex-shrink-0">
                        {user.name ? user.name.split(' ').map(n => n[0]).join('') : 'U'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-lato font-medium text-vitl-navy">{user.name || 'Unknown User'}</h3>
                        <div className="flex flex-col space-y-1 text-sm text-vitl-dark/70 md:flex-row md:items-center md:space-y-0 md:space-x-4">
                          {user.email && (
                            <div className="flex items-center space-x-1">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{user.email}</span>
                            </div>
                          )}
                          {user.phone && (
                            <div className="flex items-center space-x-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              <span>{user.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:flex-col md:items-end md:space-y-2">
                      <div className="flex items-center space-x-2 md:flex-col md:items-end md:space-x-0 md:space-y-2">
                        {user.roles && user.roles.length > 0 && (
                          <div className="flex flex-wrap gap-1 md:justify-end">
                            {user.roles.map((role, index) => (
                              <span 
                                key={`${role}-${index}`}
                                className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(role)}`}
                              >
                                {role}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-vitl-green"></div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-vitl-navy hover:text-vitl-green"
                          onClick={() => handleEditUser(user)}
                          disabled={loadingUserDetails}
                        >
                          {loadingUserDetails ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Edit className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteUser(user)}
                          disabled={loading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Empty State */}
            {users.length === 0 && !loading && !clinicsLoading && !isSearching && (
              <div className="text-center py-12">
                <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-syne font-medium text-vitl-navy mb-2">
                  No users found
                </h3>
                <p className="text-vitl-dark/70 font-lato">
                  {searchTerm ? 'Try adjusting your search terms.' : 'Add your first user to get started.'}
                </p>
              </div>
            )}
          </div>
        )}


        {/* Edit User Modal */}
        <EditUserModal
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleUpdateUser}
          loading={loading}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={!!userToDelete}
          onClose={cancelDeleteUser}
          onConfirm={confirmDeleteUser}
          title="Delete User"
          entityName={userToDelete?.name || ''}
          entityDetails={userToDelete ? `${userToDelete.email} • ${userToDelete.role}` : ''}
          message="This action cannot be undone and will permanently remove the user's access to the platform, including all permissions and associated data."
          confirmText="Delete User"
          cancelText="Cancel"
          variant="danger"
          loading={isDeleting}
        />
      </main>
    </div>
  );
};

export default Users;
